/**
 * The Pages header's New control, the browser's counterpart of the desktop
 * title bar's "+": New page as the main action, the other shareable document
 * types in its menu. The tree builds the menu (`registerCreateMenu`), because
 * the catalog filtering and the name dialog live there; this only shows it.
 * Place type is on the tree's own section and empty-space menus, as on the
 * desktop.
 */
import { useState } from 'react';
import {
  autoUpdate,
  flip,
  FloatingPortal,
  offset,
  shift,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
  useRole,
} from '@floating-ui/react';
import type { CollabSidebarCreateMenu } from '@nimbalyst/collab-bundle/docs-ui';
import { DecorativeIcon } from '../organization/DecorativeIcon';

const CONTROL = 'pages-create-button flex items-center gap-1 shrink-0 h-[26px] px-2 border border-nim bg-transparent text-[12px] text-nim cursor-pointer hover:bg-nim-hover aria-expanded:bg-nim-hover';

export function PagesCreateMenu({ menu }: { menu: CollabSidebarCreateMenu | null }) {
  const [open, setOpen] = useState(false);
  const floating = useFloating({
    open,
    onOpenChange: setOpen,
    placement: 'bottom-end',
    whileElementsMounted: autoUpdate,
    middleware: [offset(6), flip({ padding: 8 }), shift({ padding: 8 })],
  });
  const interactions = useInteractions([
    useClick(floating.context),
    useDismiss(floating.context),
    useRole(floating.context, { role: 'menu' }),
  ]);
  if (!menu) return null;
  return (
    <div className="pages-create-menu flex items-center">
      <button
        type="button"
        className={`${CONTROL} rounded-l-md`}
        onClick={menu.onPrimary}
        data-testid="pages-new-page"
      >
        <DecorativeIcon icon="add" size={16} />
        New page
      </button>
      {menu.items.length > 0 && (
        <button
          ref={floating.refs.setReference}
          {...interactions.getReferenceProps()}
          type="button"
          className={`${CONTROL} -ml-px rounded-r-md`}
          aria-label="New document of another type"
          aria-expanded={open}
          data-testid="pages-new-menu"
        >
          <DecorativeIcon icon="expand_more" size={15} className="text-nim-muted" />
        </button>
      )}
      {open && (
        <FloatingPortal>
          <div
            ref={floating.refs.setFloating}
            style={floating.floatingStyles}
            {...interactions.getFloatingProps()}
            className="pages-create-menu-list z-[100] min-w-[200px] rounded-[9px] border border-nim bg-nim p-1.5 text-nim shadow-[var(--console-shadow-lg)]"
          >
            {menu.items.map((item) => (
              <button
                key={item.id}
                type="button"
                role="menuitem"
                className="flex w-full cursor-pointer items-center gap-2 rounded-[5px] border-none bg-transparent px-2 py-[6px] text-left text-[12px] text-nim hover:bg-nim-hover"
                onClick={() => {
                  setOpen(false);
                  item.onSelect();
                }}
              >
                <DecorativeIcon icon={item.icon || 'description'} size={16} className="text-nim-muted" />
                <span className="flex-1 truncate">{item.label}</span>
              </button>
            ))}
          </div>
        </FloatingPortal>
      )}
    </div>
  );
}
